import React from "react"
import { Flex, FlexProps, Box, useDisclosure } from "@chakra-ui/core"
import styled from "@emotion/styled"
import { AssetValue } from "contentful-layout-field"
import Image, { FluidObject } from "gatsby-image"
import ReactImageMagnify from "react-image-magnify"
import fill from "lodash/fill"

import CenteredImage from "../CenteredImage"
import { Button } from "../Buttons"
import SliderModal from "../SliderModal"
import { LaptopUpOnly, LaptopDownOnly } from "../../utils/MediaQueries"

const MAX_IMAGE_BLOCKS = 3

type Asset = FluidObject & { raw: FluidObject }

export interface BikeImagesProps {
  data: Array<any>
  imageMap: Record<string, Asset>
}

const CarouselWrapper = styled(Box)`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: auto;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  scroll-snap-type: x mandatory;
`

const CarouselItem = styled(Box)`
  height: 100%;
  scroll-margin: 0px;
  scroll-snap-align: start;
  scroll-snap-stop: normal;
`

const ImageMagnify: React.FC<{ image: Asset; zoom?: string }> = ({
  image,
  zoom = "100%",
}) => (
  <ReactImageMagnify
    smallImage={{
      src: image.srcWebp || image.src,
      srcSet: image.srcSetWebp || image.srcSet,
      sizes: image.sizes,
      isFluidWidth: true,
    }}
    largeImage={{
      src: image.srcWebp || image.src,
      width: 1440,
      height: 1440 * (1 / image.aspectRatio),
    }}
    shouldUsePositiveSpaceLens
    imageStyle={{
      borderRadius: "0.5rem",
      overflow: "hidden",
    }}
    lensStyle={{
      borderRadius: "0.5rem",
    }}
    enlargedImageContainerStyle={{
      backgroundColor: "white",
      zIndex: 1000,
      borderRadius: "0.5rem",
    }}
    enlargedImageStyle={{
      maxWidth: "none",
    }}
    enlargedImageContainerDimensions={{
      width: zoom,
      height: zoom,
    }}
  />
)

const BikeImages: React.FC<BikeImagesProps & FlexProps> = ({
  data = [],
  imageMap,
  ...flexProps
}) => {
  const [showMoreImages, setShowMoreImages] = React.useState<boolean>(false)
  const toggleShowMoreImages = () => setShowMoreImages(!showMoreImages)
  const transformImage = (image: AssetValue) => imageMap[image.assetId || ""]
  const imageModal = useDisclosure()
  const [initialImageIndex, setInitialImageIndex] = React.useState(0)

  const onImageClick = (index: number) => () => {
    setInitialImageIndex(index)
    imageModal.onOpen()
  }

  const imageBlocks = data
    .map((item) => {
      if (item.layoutId === "single-image") {
        const asset = transformImage(item.image)
        return asset
          ? {
              ...item,
              asset: transformImage(item.image),
            }
          : null
      }
      if (item.layoutId === "side-by-side-image") {
        const leftAsset = transformImage(item["left-image"])
        const rightAsset = transformImage(item["right-image"])
        return leftAsset || rightAsset
          ? {
              ...item,
              leftAsset,
              rightAsset,
            }
          : null
      }
      return null
    })
    .filter((x) => !!x)
  const { totalCount, showMoreCount, allImages } = imageBlocks.reduce<{
    totalCount: number
    showMoreCount: number
    allImages: FluidObject[]
  }>(
    (acc, cur, index) => {
      if (cur.layoutId === "single-image") {
        acc.totalCount += 1
        acc.allImages.push(cur.asset)
        if (index >= MAX_IMAGE_BLOCKS) {
          acc.showMoreCount += 1
        }
      }
      if (cur.layoutId === "side-by-side-image") {
        if (cur.leftAsset) {
          acc.totalCount += 1
          acc.allImages.push(cur.leftAsset)
          if (index >= MAX_IMAGE_BLOCKS) {
            acc.showMoreCount += 1
          }
        }
        if (cur.rightAsset) {
          acc.totalCount += 1
          acc.allImages.push(cur.rightAsset)
          if (index >= MAX_IMAGE_BLOCKS) {
            acc.showMoreCount += 1
          }
        }
        return acc
      }
      return acc
    },
    { totalCount: 0, showMoreCount: 0, allImages: [] }
  )

  const [imageActiveStates, setImageActiveStates] = React.useState<
    Array<boolean>
  >(fill(new Array(totalCount), false).map((x, i) => i === 0))

  const scrollRef = React.useRef<HTMLElement>()

  const scrollListener = () => {
    const element = scrollRef.current
    if (!element) {
      console.log("scroll listener: element undefined")
    } else {
      const currentPosition = element.scrollLeft
      const totalWidth = element.scrollWidth - element.clientWidth
      const imageScrollWidth = totalWidth / totalCount + 1
      let currentIndex = Math.floor(currentPosition / imageScrollWidth)
      if (currentIndex < 0) currentIndex = 0
      if (currentIndex >= totalCount) currentIndex = totalCount - 1
      setImageActiveStates(imageActiveStates.map((x, i) => i === currentIndex))
    }
  }
  React.useEffect(() => {
    scrollRef.current?.addEventListener("scroll", scrollListener)
    return () =>
      scrollRef.current?.removeEventListener("scroll", scrollListener)
  }, [scrollRef.current])

  return (
    <Flex flexDir="column" {...flexProps}>
      <LaptopDownOnly>
        <CarouselWrapper w="100%" mt="2rem" h="16.25rem" ref={scrollRef}>
          <Flex w={`${totalCount || 1}00%`} h="100%">
            {imageBlocks.map((value, i) => {
              const STYLE = {
                width: "100%",
              }
              switch (value.layoutId) {
                case "single-image": {
                  const asset: Asset | undefined = value?.asset
                  return (
                    asset && (
                      <CarouselItem
                        key={i}
                        {...STYLE}
                        onClick={onImageClick(allImages.indexOf(value.asset))}
                      >
                        <CenteredImage
                          style={{ height: "100%" }}
                          fluid={value.asset}
                        />
                      </CarouselItem>
                    )
                  )
                }
                case "side-by-side-image": {
                  return (
                    <React.Fragment key={i}>
                      {value.leftAsset && (
                        <CarouselItem
                          {...STYLE}
                          onClick={onImageClick(
                            allImages.indexOf(value.leftAsset)
                          )}
                        >
                          <CenteredImage
                            style={{ height: "100%" }}
                            fluid={value.leftAsset}
                          />
                        </CarouselItem>
                      )}
                      {value.rightAsset && (
                        <CarouselItem
                          {...STYLE}
                          onClick={onImageClick(
                            allImages.indexOf(value.rightAsset)
                          )}
                        >
                          <CenteredImage
                            style={{ height: "100%" }}
                            fluid={value.rightAsset}
                          />
                        </CarouselItem>
                      )}
                    </React.Fragment>
                  )
                }
                default: {
                  return null
                }
              }
            })}
          </Flex>
        </CarouselWrapper>
        <Flex m="1.25rem 0 2rem 0" justifyContent="center" alignItems="center">
          {imageActiveStates.map((active, index) => (
            <Box
              key={index}
              h={active ? "0.5rem" : "0.25rem"}
              w={active ? "0.5rem" : "0.25rem"}
              bg={active ? "night" : "dawn"}
              m="0 0.5rem"
              borderRadius="0.5rem"
            />
          ))}
        </Flex>
      </LaptopDownOnly>
      <LaptopUpOnly>
        {imageBlocks.map((value, index) => {
          if (index < MAX_IMAGE_BLOCKS || showMoreImages) {
            switch (value.layoutId) {
              case "single-image": {
                const image = value.image && transformImage(value.image)
                return (
                  image && (
                    <Box
                      key={value.id}
                      width="100%"
                      mb="2.75rem"
                      onClick={onImageClick(allImages.indexOf(image))}
                    >
                      <ImageMagnify image={image} />
                    </Box>
                  )
                )
              }
              case "side-by-side-image": {
                const leftImage =
                  value["left-image"] && transformImage(value["left-image"])
                const rightImage =
                  value["right-image"] && transformImage(value["right-image"])
                return (
                  <Flex
                    key={value.id}
                    justifyContent="space-between"
                    pb="2.75rem"
                  >
                    {leftImage && (
                      <Box
                        flexGrow={1}
                        mr="1.5rem"
                        onClick={onImageClick(allImages.indexOf(leftImage))}
                      >
                        <ImageMagnify image={leftImage} zoom="200%" />
                      </Box>
                    )}
                    {rightImage && (
                      <Box
                        flexGrow={1}
                        onClick={onImageClick(allImages.indexOf(rightImage))}
                      >
                        <ImageMagnify image={rightImage} zoom="200%" />
                      </Box>
                    )}
                  </Flex>
                )
              }
              default: {
                return null
              }
            }
          }
          return null
        })}
        {imageBlocks.length > MAX_IMAGE_BLOCKS && (
          <Button
            theme="tertiary"
            color="dawn"
            fontWeight="500"
            h="4rem"
            onClick={toggleShowMoreImages}
          >
            {`View ${showMoreCount} ${!showMoreImages ? "more" : "less"}`}
          </Button>
        )}
      </LaptopUpOnly>
      <SliderModal
        isOpen={imageModal.isOpen}
        onClose={imageModal.onClose}
        images={allImages}
        closeOnOverlayClick={false}
        initialIndex={initialImageIndex}
      />
    </Flex>
  )
}

export default BikeImages
